/**
 * Assisted eKYC application state machine — contracts-1 §5, contracts-2 Appendix B.
 *
 * Both reference implementations duplicate an inline `statusList` array across three components
 * with no enum file. This is that enum, plus the transition table the workflow is enforced from.
 */

export enum EkycRequestStatus {
  DRAFT = 'DRAFT',
  SUBMITTED = 'SUBMITTED',
  BRANCH_REVIEW = 'BRANCH_REVIEW',
  BRANCH_APPROVED = 'BRANCH_APPROVED',
  COGB_REVIEW = 'COGB_REVIEW',
  COGB_APPROVED = 'COGB_APPROVED',
  CBS_PENDING = 'CBS_PENDING',
  ACCOUNT_CREATED = 'ACCOUNT_CREATED',
  SERVICE_ENROLLMENT_PENDING = 'SERVICE_ENROLLMENT_PENDING',
  COMPLETED = 'COMPLETED',
  RETURNED_TO_MAKER = 'RETURNED_TO_MAKER',
  REJECTED = 'REJECTED',
  SCREENING_HOLD = 'SCREENING_HOLD',
  VERIFICATION_FAILED = 'VERIFICATION_FAILED',
  CBS_FAILED = 'CBS_FAILED',
  EXPIRED = 'EXPIRED',
  CANCELLED = 'CANCELLED',
}

export type EkycSeverity = 'info' | 'success' | 'warning' | 'danger';

const SEVERITY: Record<EkycRequestStatus, EkycSeverity> = {
  [EkycRequestStatus.DRAFT]: 'info',
  [EkycRequestStatus.SUBMITTED]: 'info',
  [EkycRequestStatus.BRANCH_REVIEW]: 'info',
  [EkycRequestStatus.COGB_REVIEW]: 'info',
  [EkycRequestStatus.CBS_PENDING]: 'info',
  [EkycRequestStatus.SERVICE_ENROLLMENT_PENDING]: 'info',
  [EkycRequestStatus.BRANCH_APPROVED]: 'success',
  [EkycRequestStatus.COGB_APPROVED]: 'success',
  [EkycRequestStatus.ACCOUNT_CREATED]: 'success',
  [EkycRequestStatus.COMPLETED]: 'success',
  [EkycRequestStatus.RETURNED_TO_MAKER]: 'warning',
  [EkycRequestStatus.SCREENING_HOLD]: 'warning',
  [EkycRequestStatus.VERIFICATION_FAILED]: 'warning',
  [EkycRequestStatus.EXPIRED]: 'warning',
  [EkycRequestStatus.REJECTED]: 'danger',
  [EkycRequestStatus.CBS_FAILED]: 'danger',
  [EkycRequestStatus.CANCELLED]: 'danger',
};

export function statusLabel(status: EkycRequestStatus | string | null | undefined): string {
  if (!status) return '';
  return String(status)
    .replaceAll('_', ' ')
    .toLowerCase()
    .replace(/\b\w/g, (c) => c.toUpperCase());
}

export function statusSeverity(status: EkycRequestStatus | string | null | undefined): EkycSeverity {
  return SEVERITY[status as EkycRequestStatus] ?? 'info';
}

export const EKYC_STATUS_OPTIONS = Object.values(EkycRequestStatus).map((value) => ({
  name: statusLabel(value),
  value,
}));

/** Terminal states — no further workflow action is possible. */
export const TERMINAL_STATUSES: EkycRequestStatus[] = [
  EkycRequestStatus.REJECTED,
  EkycRequestStatus.CANCELLED,
  EkycRequestStatus.EXPIRED,
  EkycRequestStatus.COMPLETED,
];

export type EkycAction = 'APPROVE' | 'RETURN' | 'REJECT';
export type EkycListType = 'all' | 'maker' | 'checker' | 'cogb';

/**
 * Which tier acts on which state. The reference infers the maker/checker step from the record's
 * current status alone, which is why nothing there stops a maker checking their own record;
 * segregation of duties is enforced separately in EkycWorkflowService.
 */
export const ACTIONABLE_BY: Record<'checker' | 'cogb', EkycRequestStatus[]> = {
  checker: [EkycRequestStatus.BRANCH_REVIEW],
  cogb: [EkycRequestStatus.COGB_REVIEW],
};

export function nextStatusFor(
  current: EkycRequestStatus,
  action: EkycAction,
  tier: 'checker' | 'cogb',
): EkycRequestStatus | null {
  if (!ACTIONABLE_BY[tier].includes(current)) {
    return null;
  }
  if (action === 'RETURN') return EkycRequestStatus.RETURNED_TO_MAKER;
  if (action === 'REJECT') return EkycRequestStatus.REJECTED;
  return tier === 'checker' ? EkycRequestStatus.BRANCH_APPROVED : EkycRequestStatus.COGB_APPROVED;
}

// ---------------------------------------------------------------------------------------------

export enum ScreeningStatus {
  NOT_RUN = 'NOT_RUN',
  CLEAR = 'CLEAR',
  POTENTIAL_MATCH = 'POTENTIAL_MATCH',
  CONFIRMED_MATCH = 'CONFIRMED_MATCH',
}

export const SCREENING_STATUS_OPTIONS = Object.values(ScreeningStatus).map((value) => ({
  name: statusLabel(value),
  value,
}));

export function screeningSeverity(status: ScreeningStatus | string | null | undefined): EkycSeverity {
  switch (status) {
    case ScreeningStatus.CLEAR:
      return 'success';
    case ScreeningStatus.POTENTIAL_MATCH:
      return 'warning';
    case ScreeningStatus.CONFIRMED_MATCH:
      return 'danger';
    default:
      return 'info';
  }
}

export type RiskBandName = 'LOW' | 'MEDIUM' | 'HIGH';

export const RISK_BAND_OPTIONS: { name: string; value: RiskBandName }[] = [
  { name: 'Low', value: 'LOW' },
  { name: 'Medium', value: 'MEDIUM' },
  { name: 'High', value: 'HIGH' },
];

export function riskBandSeverity(band: RiskBandName | string | null | undefined): EkycSeverity {
  switch (band) {
    case 'LOW':
      return 'success';
    case 'MEDIUM':
      return 'warning';
    case 'HIGH':
      return 'danger';
    default:
      return 'info';
  }
}

// ---------------------------------------------------------------------------------------------

export enum ServiceRequestType {
  DEBIT_CARD = 'DEBIT_CARD',
  CHEQUE_BOOK = 'CHEQUE_BOOK',
  INTERNET_BANKING = 'INTERNET_BANKING',
}

export const SERVICE_TYPE_OPTIONS = [
  { name: 'Debit Card', value: ServiceRequestType.DEBIT_CARD },
  { name: 'Cheque Book', value: ServiceRequestType.CHEQUE_BOOK },
  { name: 'Internet Banking', value: ServiceRequestType.INTERNET_BANKING },
];

export enum ServiceRequestStatus {
  PENDING = 'PENDING',
  SUBMITTED = 'SUBMITTED',
  ACKNOWLEDGED = 'ACKNOWLEDGED',
  FAILED = 'FAILED',
  RETRY = 'RETRY',
  MANUALLY_CLOSED = 'MANUALLY_CLOSED',
}

export const SERVICE_STATUS_OPTIONS = Object.values(ServiceRequestStatus).map((value) => ({
  name: statusLabel(value),
  value,
}));

export function serviceStatusSeverity(status: ServiceRequestStatus | string | null | undefined): EkycSeverity {
  switch (status) {
    case ServiceRequestStatus.ACKNOWLEDGED:
      return 'success';
    case ServiceRequestStatus.FAILED:
      return 'danger';
    case ServiceRequestStatus.RETRY:
    case ServiceRequestStatus.MANUALLY_CLOSED:
      return 'warning';
    default:
      return 'info';
  }
}
